import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AvatarItemModel } from 'src/app/bar/models/avatar-item.model';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { Verifications, Wiki } from '@local/client-contracts';
import { isEqual } from 'lodash';
import moment from 'moment';
import { DateFormat } from '@shared/consts';
import { StatsService } from 'src/app/bar/services/stats.service';
import { WikiCardsService } from 'src/app/bar/services/wikis/wiki-cards.service';
import { UiIconModel } from '@local/ui-infra';
import { WikiCardPageMode } from '../../models/wiki-card-model';
import { HubService } from 'src/app/bar/services/hub.service';
import { StatVal } from '@local/client-contracts/src/stats';

@UntilDestroy()
@Component({
  selector: 'wiki-card-sub-header',
  templateUrl: './wiki-card-sub-header.component.html',
  styleUrls: ['./wiki-card-sub-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WikiCardSubHeaderComponent implements OnInit {
  private readonly VERIFIED_ICON: string = './assets/verification/verified-light.svg';
  private readonly UNVERIFIED_ICON: string = './assets/verification/unverified-light.svg';
  private readonly CREATED_MESSAGE = 'You created this few seconds ago';
  private _collection: Wiki.WikiCollection;
  private _wikiCard: Wiki.Card;
  private _updatedVerification: Verifications.Verification;

  avatarsList: AvatarItemModel[];
  views: string;
  verificationIcon: UiIconModel;
  verificationTime: string;
  isVerified: boolean;
  headerMessage: string;
  publishedTimeTooltip: string;

  @Input() pageMode: WikiCardPageMode;
  @Input() set updatedVerification(value: Verifications.Verification) {
    this._updatedVerification = value;
    if (this.updatedVerification) {
      this.isVerified = this.updatedVerification.status === 'Verified';
      this.verificationIcon = {
        type: 'img',
        value: { lightUrl: this.isVerified ? this.VERIFIED_ICON : this.UNVERIFIED_ICON },
      };
      this.verificationTime = this.getVerificationTime();
    }
    this.cdr.markForCheck();
  }

  @Input() set collection(val: Wiki.WikiCollection) {
    const collectionChanged = !isEqual(this._collection, val);
    this._collection = val;
    if (this.wikiCard || collectionChanged) {
      this.createAvatarList();
    }
    this.cdr.markForCheck();
  }

  @Input() set wikiCard(val: Wiki.Card) {
    this._wikiCard = val;

    if (val) {
      this.getViews();
      this.createAvatarList();
      this.setHeaderText();
    }

    this.cdr.markForCheck();
  }

  get wikiCard() {
    return this._wikiCard;
  }

  get collection() {
    return this._collection;
  }

  get updatedVerification(): Verifications.Verification {
    return this._updatedVerification;
  }

  get viewsInfo() {
    return `| ${this.views}`;
  }

  @HostBinding('attr.show-verification')
  get showVerificationIndications(): boolean {
    return this.collection?.policy?.disabled === false;
  }

  @HostBinding('attr.pageMode')
  get pageModeAttr() {
    return this.pageMode;
  }

  constructor(
    private collectionsService: CollectionsService,
    private cdr: ChangeDetectorRef,
    private statsService: StatsService,
    private wikiCardsService: WikiCardsService,
    private hubService: HubService
  ) {}

  ngOnInit(): void {
    this.statsService.updated$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.wikiCard?.id) {
        this.getViews();
      }
    });
  }

  private getVerificationTime(): string {
    const time = moment(this.isVerified ? this.updatedVerification?.verifiedUntil : this.updatedVerification?.statusChangedAt).format(
      DateFormat.FULL_DATE
    );
    return `${this.isVerified ? 'Verified Until' : 'Unverified since'} ${time}`;
  }

  private async setHeaderText() {
    const accountId = this.wikiCard?.id ? this.wikiCard.publishedBy : this.collectionsService.sessionInfo?.user?.id;

    const timePhrase = await this.hubService.getTimePhrases({
      modifiedBy: accountId,
      modifiedTime: this.wikiCard.publishedTime,
    });

    this.headerMessage = this.wikiCard?.publishedTime ? `${timePhrase.name} published this ${timePhrase.timeText}` : this.CREATED_MESSAGE;

    this.publishedTimeTooltip = timePhrase.fullDetailsTime;

    this.cdr.markForCheck();
  }

  private async createAvatarList() {
    const avatar = await this.collectionsService.getCurrentCardAvatar(this.collection, this.wikiCard);
    this.avatarsList = [avatar];
    this.cdr.markForCheck();
  }

  private async getViews() {
    const stat: StatVal = (await this.statsService.getViewsById(new Set<string>([this.wikiCard.id])))?.[0];
    if (stat) {
      this.views = `${stat.views} Views`;
      this.cdr.markForCheck();
    }
  }

  openVerificationDetails() {
    this.changeFocus('card-verification-details');
    this.wikiCardsService.openVerificationDetails(
      this.wikiCard.id,
      this.collection,
      this.updatedVerification?.policy,
      this.updatedVerification,
      this.wikiCard.title
    );
  }

  changeFocus(focus: string) {
    this.collectionsService.onFocusChange = focus;
  }
}
